import { useState } from 'react';

import { FullScreenLoading } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CategoryAnswersView } from '@backrs_ui/grow-ui/src/pages/LifeReadiness/CategoryAnswersView';
import { LifeReadinessDashboard } from '@backrs_ui/grow-ui/src/pages/LifeReadiness/LifeReadinessDashboard';
import {
  useGetCategoriesQuery,
  useGetQuestionsQuery,
  useGetQuestionUserLevelsQuery,
  useGetSubcategoriesQuery,
} from '@backrs_ui/grow-ui/src/redux/lifeReadiness/lifeReadiness.api';

import { Box, type SxProps, type Theme } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import { useAgentContext } from './MemberLifeReadinessView.agentContext';
import { MemberTabErrorState, TabErrorMessage } from './MemberTabErrorState';
import { useCurrentMember } from './useCurrentMember';

const {
  modules: { coachingDashboard },
} = appStrings;

export const MemberLifeReadinessView = () => {
  // Swapping content in place rather than routing — routing away closes the tab.
  const [openCategoryId, setOpenCategoryId] = useState<number | null>(null);

  const currentMember = useCurrentMember();
  const { member, isLoading: isLoadingMember, isError: isMemberError } = currentMember;

  const {
    data: userLevels,
    isLoading: isLoadingUserLevels,
    isError,
  } = useGetQuestionUserLevelsQuery(member ? member.id : skipToken);

  const { data: categories, isLoading: isLoadingCategories } = useGetCategoriesQuery(undefined);
  const { data: subcategories, isLoading: isLoadingSubcategories } =
    useGetSubcategoriesQuery(undefined);
  const { data: questions, isLoading: isLoadingQuestions } = useGetQuestionsQuery(undefined);

  useAgentContext(member);

  if (isMemberError) return <MemberTabErrorState {...currentMember} />;

  if (
    isLoadingMember ||
    isLoadingUserLevels ||
    isLoadingCategories ||
    isLoadingSubcategories ||
    isLoadingQuestions
  ) {
    return <FullScreenLoading fillContainer />;
  }

  if (!member) return null;

  if (isError || !userLevels || !categories || !subcategories || !questions) {
    return <TabErrorMessage message={coachingDashboard.lifeReadinessError} />;
  }

  const openCategory = categories.find((category) => category.id === openCategoryId);

  return (
    <Box sx={containerSx}>
      {openCategory ? (
        <CategoryAnswersView
          category={openCategory}
          subcategories={subcategories}
          questions={questions}
          userLevels={userLevels}
          onBack={() => setOpenCategoryId(null)}
        />
      ) : (
        <LifeReadinessDashboard
          categories={categories}
          subcategories={subcategories}
          questions={questions}
          userLevels={userLevels}
          subject={{ name: `${member.first_name} ${member.last_name}` }}
          onCategoryClick={(category) => setOpenCategoryId(category.id)}
        />
      )}
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingRight: backrs.spacing['spacing-md'],
});
